import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { ChatModal } from "./ChatModal";
import { useIsMobile, useIsMobileOrTablet } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

export function ChatButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const isMobile = useIsMobile();
  const isMobileOrTablet = useIsMobileOrTablet();
  const navigate = useNavigate();

  // Listen for new assistant messages when chat is closed
  useEffect(() => {
    const conversationId = localStorage.getItem("pixie_conversation_id");
    if (!conversationId || isOpen) {
      return;
    }

    const channel = supabase
      .channel(`unread-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          if (payload.new && (payload.new as { role: string }).role === 'assistant') {
            setHasUnread(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen]);

  const handleClick = () => {
    setHasUnread(false);
    if (isMobileOrTablet) {
      navigate("/chat");
    } else {
      setIsOpen(true);
    }
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClick}
            className="fixed z-50 bottom-6 right-6 flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-shadow hover:shadow-xl h-14 w-14"
            aria-label="Open chat"
          >
            <MessageCircle className="h-6 w-6" />
            
            {/* Unread indicator */}
            {hasUnread && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-4 w-4 bg-accent" />
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Modal - only used on desktop */}
      <ChatModal isOpen={isOpen} onClose={handleClose} />
    </>
  );
}
